import { type Device, type PurgeStateAction } from './licenseGroups.types';

export const startPagesStarter = ['dashboard', 'workflows', 'devices', 'users', 'forms', 'logs'] as const;

export const startPages = [
	'home',
	'inbox',
	'jobs',
	'job-queue',
	'tracker',
	'dashboard',
	'apps',
	'workflows',
	'reports',
	'bsn',
	'archive',
	'my-qs',
	'manage-qs',
	'devices',
	'users',
	'forms',
	'naming',
	'logs',
	'roles',
] as const;

export const quickLinks = [
	'profile',
	'help',
	'inbox',
	'job-queue',
	'job-queue-management',
	'tracker',
	'jobs',
	'add-app',
	'create-workflow',
	'import-workflows',
	'reports',
	'bsn',
	'archive',
	'my-qs',
	'manage-qs',
	'devices',
	'import-devices',
	'add-device',
	'users',
	'invite-users',
	'forms',
	'create-form',
	'naming',
	'logs',
	'roles',
	'create-role',
] as const;

export const starterQuickLinks = [
	'profile',
	'help',
	'create-workflow',
	'import-workflows',
	'devices',
	'import-devices',
	'add-device',
	'users',
	'invite-users',
	'forms',
	'create-form',
	'logs',
	'roles',
	'create-role',
] as const;

export const bizQuickLinks = [
	'profile',
	'help',
	'inbox',
	'tracker',
	'jobs',
	'add-app',
	'create-workflow',
	'import-workflows',
	'devices',
	'import-devices',
	'add-device',
	'users',
	'invite-users',
	'forms',
	'create-form',
	'logs',
	'roles',
	'create-role',
] as const;

export const retentionOptions = [1, 7, 30, 90, 180, 365];

export type SignatureField = {
	signatureActive?: boolean;
	signatureType?: 'typeSign' | 'drawSign' | 'uploadImageSign';
	signatureImage?: string;
	signatureText?: string;
};

export type UniversalPrintDetails = {
	userId?: string;
	profileName?: string;
	profileEmail?: string;
	profileRawId?: string;
}

export const languages = ['en', 'ja', 'fr', 'es', 'de', 'it'] as const;

export const userPreferencesMessagesNames = [
	// 'messages_msup_deviceRegistrationError',
	'messages_r2m_delegatedPrintFiles',
	'messages_r2m_addedToSharedPrintQueue',
	'messages_r2m_addedToDirectPrintQueue',
	'messages_system_printQueueBlockedList',
	'messages_r2m_directPrintDisabled',
	'messages_r2m_r2mDisabled',
	'messages_system_printQueueBlocked',
	'messages_auth_csvUpload',
	'messages_auth_mfpProvisioning',
	'messages_bsn_deviceNotSecure',
	'messages_storage_trashedFileExpired',
	'messages_storage_archivedJobExpired',
	'messages_storage_fileRetentionExpired',
	'messages_storage_fileShared',
	'messages_storage_storageDisabled',
	'messages_job_newJob',
	'messages_job_newJobAssigned',
	'messages_job_jobNeedsAttention',
	'messages_job_collabRequest',
	'messages_mfp_deviceOffline',
	'messages_mfp_deviceError',
	'messages_mfp_emptyPaperTray',
	'messages_mfp_lowTonerLevel',
	'messages_wfx_running',
	'messages_wfx_stopped',
	'messages_wfx_paused',
] as const;

export type UserPreferencesMessagesName = (typeof userPreferencesMessagesNames)[number];

export type UserPreferences = {
	startPage: (typeof startPages)[number];
	homePageLayout: 'rows' | 'columns' | 'grid' | 'left';
	quickLinks: [
		(typeof quickLinks)[number] | undefined,
		(typeof quickLinks)[number] | undefined,
		(typeof quickLinks)[number] | undefined,
	];
	appearance: 'dark' | 'light' | 'system';
	// background image URL
	siteBackground: string;
	tableHeaderBackground: 'standard' | 'color';
	tableHeaderStandardBackgroundOpacity: number;
	tableHeaderColorBackgroundOpacity: number;
	pageHeaderBackgroundOpacity: number;
	navbarBackgroundOpacity: number;
	messageRetentionDaysInfo: number;
	messageRetentionDaysError: number;
	messageRetentionDaysJob: number;
	messageRetentionDaysOther: number;
	language: (typeof languages)[number];
} & {
	[key in UserPreferencesMessagesName as `${key}_email`]: boolean;
} & {
	[key in UserPreferencesMessagesName as `${key}_inbox`]: boolean;
};

export type Profile = {
	id: string;
	slug: string;
	firstName: string;
	lastName: string;
	email: string;
	username: string;
	mpData: {
		firstName: string;
		lastName: string;
		email: string;
	};
	mpId?: string;
	country: string;
	awsRegion: 'us-east-1' | 'ca-central-1' | 'eu-central-1' | 'ap-northeast-1';
	tenantId?: string;
	tokens: {
		accessToken: string;
		refreshToken: string;
		idToken: string;
		expirationDate: number;
	};
	guestUser?: string;
	preferences: UserPreferences;
	appData: {
		lastAcknowledgedNotificationDate: number | undefined;
		acknowledgedIds: string[];
		favoriteMFPDevices: Device['serial'][];
	};
	signature?: SignatureField;
	ippPassword?: string;
	pinNumber?: string;
	univPrintProfile?: UniversalPrintDetails;
};

export type ProfileState = {
	info: Profile;
	previousProfileId: string | undefined;
};

type GetProfileSuccessAction = {
	type: 'GET_PROFILE_SUCCESS';
	payload: {
		data: {
			user: Profile;
		};
	};
};

type GetSignatureSuccessAction = {
	type: 'GET_SIGNATURE_SUCCESS';
	payload: {
		data: {
			signature: SignatureField;
		};
	};
};

type GetUniversalPrintProfileSuccessAction = {
	type: 'GET_UNIV_PRINT_PROFILE_SUCCESS';
	payload: {
		data: {
			univPrintProfile: UniversalPrintDetails;
		};
	};
};

export type ProfileActionTypes = GetProfileSuccessAction | GetSignatureSuccessAction | GetUniversalPrintProfileSuccessAction | PurgeStateAction;
